import { TakeEffect, takeEvery } from 'redux-saga/effects';
import { createSelector } from 'reselect';
import { CallbackSaga, createGeneralCall } from '@altermaind/core/requests-status-management';
import { IS_MOCK_ENABLED } from '@/_shared/constants/mock';
import { getCurrentOrganization } from '@/core/state/selectors';
import { KEY_GET_CONTENTS_BY_TYPE, API_GET_CONTENTS_BY_TYPE } from '../constants';
import { MOCK_RESPONSE_GET_CONTENTS_BY_TYPE } from '../mocks';
import {
  API_GET_CONTENTS_BY_TYPE_REQUEST_PATH_PARAMS,
  API_GET_CONTENTS_BY_TYPE_REQUEST_QUERY_PARAMS,
  API_GET_CONTENTS_BY_TYPE_PROPS,
} from '../types';

export type RequestGetContentsByType = API_GET_CONTENTS_BY_TYPE_REQUEST_PATH_PARAMS &
  API_GET_CONTENTS_BY_TYPE_REQUEST_QUERY_PARAMS;
export type ResponseGetContentsByType = API_GET_CONTENTS_BY_TYPE_PROPS['response']['body'];

export type CallParamsGetContentsByType = Omit<RequestGetContentsByType, 'organizationId'> & {
  successSaga?: CallbackSaga;
  errorSaga?: CallbackSaga;
};

export type CallResponseGetContentsByType = ResponseGetContentsByType;

type CallErrorGetContentsByType = {
  message: string;
  code: number;
  type: string;
  title: string;
  status: number;
};

type FullRequestType = ReturnType<typeof createGeneralCall>['selectors']['request'] & {
  payload: Omit<CallParamsGetContentsByType, 'successSaga' | 'errorSaga'>;
};

type CallSelectorsGetContentsByType = {
  data: CallResponseGetContentsByType['data'];
  totalItems: CallResponseGetContentsByType['totalItems'];
  fullRequest: FullRequestType;
};

let internalPayload = {};

export const getContentsByTypeCall = createGeneralCall<
  CallParamsGetContentsByType,
  CallResponseGetContentsByType,
  CallErrorGetContentsByType,
  CallSelectorsGetContentsByType
>({
  id: KEY_GET_CONTENTS_BY_TYPE,
  url:
    ({ payload }) =>
    state => {
      const org = getCurrentOrganization(state);
      return API_GET_CONTENTS_BY_TYPE(org as unknown as string, payload);
    },
  method: 'get',
  paramsSelector:
    ({ payload }) =>
    () => {
      internalPayload = { ...payload };
      return payload;
    },
  selectors: {
    data: ({ selectors }) => createSelector(selectors.response, res => res?.data || []),
    totalItems: ({ selectors }) => createSelector(selectors.response, res => res?.totalItems || 0),
    fullRequest: ({ selectors }) =>
      createSelector(selectors.request, request => {
        return {
          ...request,
          payload: { ...internalPayload },
        } as unknown as FullRequestType;
      }),
  },
  successMock: IS_MOCK_ENABLED() ? MOCK_RESPONSE_GET_CONTENTS_BY_TYPE : undefined,
  cancellable: true,
  takeEffect: takeEvery as unknown as TakeEffect,
  rsmOptions: {
    doubleRun: false,
  },
});
